/**
 * CalendarPage — Tutor weekly calendar.
 *
 * - Aggregates sessions across all active contracts for the visible week.
 * - Colors sessions by SessionStatus using the exact API French labels.
 * - Prev/next/today week navigation.
 * - Slot and pause management per contract (with 422 overlap surfacing).
 * - Session detail dialog with state-contextual actions.
 */

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, CalendarClock } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useWeekSessions } from '../../hooks/useWeekSessions';
import {
  getWeekStart,
  prevWeek,
  nextWeek,
  mapSessionsToWeek,
  formatWeekLabel,
} from '../../utils/weekMapping';
import WeekGrid from '../../components/calendar/WeekGrid';
import SessionDetail from '../../components/calendar/SessionDetail';
import SlotWizard from '../../components/calendar/SlotWizard';
import PauseForm from '../../components/calendar/PauseForm';
import Button from '../../components/ui/Button';

const CalendarPage = () => {
  const { toast } = useToast();

  // Current week's Monday
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));

  // Selected session for detail dialog
  const [selectedSession, setSelectedSession] = useState(null);

  // Slot/pause management state
  const [slotFormState, setSlotFormState] = useState({
    open: false,
    contract: null,
    slot: null,
  });
  const [pauseFormState, setPauseFormState] = useState({
    open: false,
    contractId: null,
    pause: null,
  });

  // Selected contract for slot/pause management
  const [managingContractId, setManagingContractId] = useState(null);

  const { sessions, contracts, loading, error, refresh } = useWeekSessions(weekStart);

  const sessionsByDay = useMemo(
    () => mapSessionsToWeek(sessions, weekStart),
    [sessions, weekStart]
  );

  const showToast = ({ message, variant }) => toast(message, variant);

  const handlePrev = () => setWeekStart((w) => prevWeek(w));
  const handleNext = () => setWeekStart((w) => nextWeek(w));
  const handleToday = () => setWeekStart(getWeekStart(new Date()));

  const openSlotForm = (contract, slot = null) => {
    setSlotFormState({ open: true, contract, slot });
  };

  const openPauseForm = (contractId, pause = null) => {
    setPauseFormState({ open: true, contractId, pause });
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-playfair font-bold text-[#17386E]">
            Calendrier
          </h1>
          <p className="text-sm font-montserrat text-[#4A4A4A] mt-1">
            Vue hebdomadaire de toutes vos séances
          </p>
        </div>

        {/* Contract selector for slot/pause management */}
        {contracts.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={managingContractId ?? ''}
              onChange={(e) =>
                setManagingContractId(e.target.value ? Number(e.target.value) : null)
              }
              className="bg-[#F5F5F5] border border-transparent rounded-xl py-2 px-3 font-montserrat text-sm text-[#4A4A4A] focus:outline-none focus:ring-2 focus:ring-[#EAF4FB]"
            >
              <option value="">— Gérer un contrat —</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>
                  Contrat #{c.id}
                  {c.subject?.name ? ` — ${c.subject.name}` : ''}
                </option>
              ))}
            </select>
            {managingContractId && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const c = contracts.find((x) => x.id === managingContractId);
                    openSlotForm(c ?? { id: managingContractId });
                  }}
                >
                  <CalendarClock size={14} className="mr-1" />
                  Programmer mes cours
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openPauseForm(managingContractId)}
                >
                  <Calendar size={14} className="mr-1" />
                  Pauses
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Week navigation bar */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={handlePrev} aria-label="Semaine précédente">
          <ChevronLeft size={18} />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleToday}>
          Aujourd'hui
        </Button>
        <Button variant="ghost" size="sm" onClick={handleNext} aria-label="Semaine suivante">
          <ChevronRight size={18} />
        </Button>
        <span className="ml-2 text-sm font-montserrat font-medium text-[#17386E]">
          {formatWeekLabel(weekStart)}
        </span>
        {loading && (
          <span className="ml-2 text-xs font-montserrat text-gray-400 animate-pulse">
            Chargement…
          </span>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-sm font-montserrat text-red-700">{error}</p>
        </div>
      )}

      {/* No contracts */}
      {!loading && !error && contracts.length === 0 && (
        <div className="bg-gray-50 rounded-2xl p-8 text-center">
          <p className="text-sm font-montserrat text-[#4A4A4A]">
            Aucun contrat actif. Le calendrier affichera vos séances une fois un contrat accepté.
          </p>
        </div>
      )}

      {/* Week grid */}
      {(contracts.length > 0 || loading) && (
        <div
          className={[
            'bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden',
            loading ? 'opacity-60 pointer-events-none' : '',
          ].join(' ')}
        >
          <WeekGrid
            weekStart={weekStart}
            sessionsByDay={sessionsByDay}
            onSessionClick={setSelectedSession}
          />
        </div>
      )}

      {/* Session detail dialog */}
      <SessionDetail
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
        onRefresh={refresh}
        showToast={showToast}
      />

      {/* Slot wizard dialog */}
      <SlotWizard
        open={slotFormState.open}
        onClose={() => setSlotFormState({ open: false, contract: null, slot: null })}
        contract={slotFormState.contract}
        slot={slotFormState.slot}
        onSuccess={refresh}
        showToast={showToast}
      />

      {/* Pause form dialog */}
      <PauseForm
        open={pauseFormState.open}
        onClose={() => setPauseFormState({ open: false, contractId: null, pause: null })}
        contractId={pauseFormState.contractId}
        pause={pauseFormState.pause}
        onSuccess={refresh}
        showToast={showToast}
      />
    </div>
  );
};

export default CalendarPage;
